import { useState } from "react";
import { Plus, X } from "lucide-react";
import { cn } from "@/utils";
import { FieldShell } from "./FieldShell";

interface TagInputProps {
  label?: React.ReactNode;
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  hint?: React.ReactNode;
  error?: string | null;
  required?: boolean;
  /** Case-insensitive de-duplication (default true). */
  dedupe?: boolean;
  containerClassName?: string;
}

/**
 * Chip-style multi-value input (skills, keywords, tags). Add with Enter or the
 * button, remove with the chip's ✕. Manages its own draft text; the committed
 * list flows through `value`/`onChange`.
 */
export function TagInput({
  label,
  value,
  onChange,
  placeholder = "Add and press Enter",
  hint,
  error,
  required,
  dedupe = true,
  containerClassName,
}: TagInputProps) {
  const [draft, setDraft] = useState("");

  const commit = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (dedupe && value.some((v) => v.toLowerCase() === trimmed.toLowerCase())) {
      setDraft("");
      return;
    }
    onChange([...value, trimmed]);
    setDraft("");
  };

  const remove = (tag: string) => onChange(value.filter((v) => v !== tag));

  return (
    <FieldShell
      label={label}
      required={required}
      hint={hint}
      error={error}
      className={containerClassName}
    >
      <div className="flex gap-2">
        <div className="auth-input flex h-11 flex-1 items-center rounded-xl px-3.5">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commit();
              }
            }}
            placeholder={placeholder}
            className="min-w-0 flex-1 border-0 bg-transparent text-sm outline-none placeholder:text-[color:var(--landing-text-dim)]"
            style={{ color: "var(--landing-text)" }}
          />
        </div>
        <button
          type="button"
          onClick={commit}
          className="btn-electric inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
          aria-label="Add"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {value.map((tag, i) => (
            <span
              key={`${tag}-${i}`}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
              )}
              style={{
                background: "rgba(var(--acc2-rgb),0.08)",
                color: "#00B0D8",
                border: "1px solid rgba(var(--acc2-rgb),0.2)",
              }}
            >
              {tag}
              <button
                type="button"
                onClick={() => remove(tag)}
                className="transition-colors hover:text-destructive"
                aria-label={`Remove ${tag}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </FieldShell>
  );
}
